import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { Search, X, Check } from "lucide-react";
import { toast } from "sonner";

export default function AgencyEmployers() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [stages, setStages] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const { data } = await api.get("/employers", { params: { q: q || undefined, stage: stage || undefined } });
    setItems(data.items || []);
  };
  useEffect(() => { load(); api.get("/employers/stages").then(r => setStages(r.data.stages)); /* eslint-disable-next-line */ }, [stage]);

  const approve = async (id, ok) => {
    await api.put(`/employers/${id}`, { is_approved: ok });
    toast.success(ok ? "Approved" : "Approval revoked");
    load();
  };

  const suspend = async (id, s) => {
    await api.put(`/employers/${id}`, { is_suspended: s });
    toast.success(s ? "Suspended" : "Reactivated");
    load();
  };

  return (
    <div>
      <div className="mb-6">
        <div className="eyebrow mb-2">Agency Owner</div>
        <h1 className="font-display text-3xl font-bold text-navy">Employer Accounts</h1>
      </div>
      <div className="flex gap-3 mb-5 flex-wrap">
        <form onSubmit={(e) => {e.preventDefault(); load();}} className="flex-1 relative min-w-[240px]">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Company, industry, city…" className="pl-9 w-full border border-slate-200 rounded-md px-3 py-2.5 text-sm" />
        </form>
        <select value={stage} onChange={e => setStage(e.target.value)} className="border border-slate-200 rounded-md px-3 py-2.5 text-sm">
          <option value="">All stages</option>
          {stages.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div data-testid={TID.agencyEmployersTable} className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-slate-500">
              <th className="p-3">Company</th><th className="p-3">Industry</th><th className="p-3">City</th>
              <th className="p-3">Stage</th><th className="p-3">Fee</th><th className="p-3">Status</th><th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(e => (
              <tr key={e.id} className="border-b last:border-0 hover:bg-slate-50">
                <td className="p-3 font-medium text-navy cursor-pointer" onClick={() => setSelected(e)}>{e.company_name}</td>
                <td className="p-3 text-slate-600">{e.industry}</td>
                <td className="p-3 text-slate-600">{e.city}{e.state ? `, ${e.state}` : ""}</td>
                <td className="p-3"><span className="px-2 py-1 rounded-full text-xs bg-slate-100">{e.pipeline_stage}</span></td>
                <td className="p-3 text-slate-600">{e.fee_type ? `${e.fee_type}: ${e.fee_value ?? ""}` : "—"}</td>
                <td className="p-3">
                  {e.is_suspended ? <span className="text-red-500 text-xs">Suspended</span>
                   : e.is_approved ? <span className="text-emerald-600 text-xs">Approved</span>
                   : <span className="text-amber-600 text-xs">Pending</span>}
                </td>
                <td className="p-3 text-xs">
                  {!e.is_approved && <button onClick={() => approve(e.id, true)} className="text-emerald-600 mr-2">Approve</button>}
                  {e.is_approved && !e.is_suspended && <button onClick={() => suspend(e.id, true)} className="text-red-500 mr-2">Suspend</button>}
                  {e.is_suspended && <button onClick={() => suspend(e.id, false)} className="text-navy">Reactivate</button>}
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td className="p-6 text-center text-slate-500" colSpan={7}>No employers yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setSelected(null)}>
          <div className="w-full md:w-[520px] bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6 border-b flex items-center justify-between">
              <div className="font-display text-xl font-semibold text-navy">{selected.company_name}</div>
              <button onClick={() => setSelected(null)} className="text-slate-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4 text-sm">
              <div><div className="eyebrow mb-1">About</div><div className="text-slate-600">{selected.description || "—"}</div></div>
              <div><div className="eyebrow mb-1">Location</div><div>{selected.city}{selected.state ? `, ${selected.state}` : ""}</div></div>
              <div><div className="eyebrow mb-1">Website</div><a href={selected.website} className="text-navy">{selected.website || "—"}</a></div>
              <div>
                <div className="eyebrow mb-1">Contacts</div>
                {(selected.contacts || []).map((c, i) => (
                  <div key={i} className="text-slate-700">
                    <div className="font-medium">{c.name} <span className="text-slate-500 text-xs">({c.title})</span></div>
                    <div className="text-xs text-slate-500">{c.email} • {c.phone}</div>
                  </div>
                ))}
              </div>
              <div>
                <div className="eyebrow mb-1">Fee</div>
                <div className="flex gap-2">
                  <select defaultValue={selected.fee_type} onChange={async e => { await api.put(`/employers/${selected.id}`, { fee_type: e.target.value }); load(); }}
                    className="border rounded px-2 py-1 text-sm">
                    <option value="">—</option>
                    <option value="flat">Flat placement</option>
                    <option value="percentage">Percentage</option>
                    <option value="contract_markup">Contract markup</option>
                  </select>
                  <input type="number" defaultValue={selected.fee_value || ""} onBlur={async e => { await api.put(`/employers/${selected.id}`, { fee_value: parseFloat(e.target.value) || 0 }); load(); }}
                    className="border rounded px-2 py-1 text-sm w-32" placeholder="Amount" />
                </div>
              </div>
              <div>
                <div className="eyebrow mb-1">Private notes</div>
                <textarea rows={3} defaultValue={selected.internal_notes} onBlur={async e => { await api.put(`/employers/${selected.id}`, { internal_notes: e.target.value }); toast.success("Saved"); }}
                  className="w-full border rounded px-2 py-1 text-sm" />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
